"use client";

import { AuthFormHeading } from "./auth-form-heading"

import type { ComponentProps } from "react";
import Link from "next/link";

import { FieldInputOTP, FieldPassword, FieldText } from "@/shared/components/form/form-fields";
import { LoadingButton } from "@/shared/components/feedback/loading-button";
import { Form, FormLabel } from "@/shared/components/ui/form";
import { cn } from "@/shared/utils/cn";

import { useSignInForm } from "../hooks/use-sign-in-form";

interface MfaStepProps {
  errorMessage: string;
  isSubmitting: boolean;
  isCodeComplete: boolean;
  onCodeChange: () => void;
}

interface CredentialsStepProps {
  errorMessage: string;
  isSubmitting: boolean;
}

function MfaStep({
  errorMessage,
  isSubmitting,
  isCodeComplete,
  onCodeChange,
}: MfaStepProps) {
  return (
    <div className="flex flex-col gap-12">
      <FieldInputOTP
        name="code"
        className="justify-items-center"
        gapClassName="space-x-2"
        slotClassName="rounded-md border-l"
        onValueChange={onCodeChange}
      />
      {errorMessage && (
        <p role="alert" className="mt-3 text-center text-sm text-destructive">{errorMessage}</p>
      )}

      <LoadingButton
        type="submit"
        className="mt-6 w-full"
        loading={isSubmitting}
        disabled={!isCodeComplete}
      >
        Doğrula
      </LoadingButton>
    </div>
  );
}

function CredentialsStep({ errorMessage, isSubmitting }: CredentialsStepProps) {
  return (
    <>
      <div className="flex flex-col gap-6">
        <FieldText name="tenant" label="Müşteri Kodu" autoComplete="organization" />
        <FieldText name="email" label="E-posta" type="email" autoComplete="email" />

        <FieldPassword
          name="password"
          autoComplete="current-password"
          showVisibilityToggle
          label={
            <div className="flex items-center">
              <FormLabel>Şifre</FormLabel>
              <Link
                href="/forgot-password"
                className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
              >
                Şifrenizi mi unuttunuz?
              </Link>
            </div>
          }
        />

        {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}

        <LoadingButton type="submit" className="w-full" loading={isSubmitting}>
          Giriş Yap
        </LoadingButton>

        <p className="text-center text-sm text-muted-foreground">
          Hesabınız yok mu?{" "}
          <Link
            href="/kayit"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            Kayıt olun
          </Link>
        </p>
      </div>
    </>
  );
}

export function SignIn({ className, ...props }: ComponentProps<"div">) {
  const {
    form,
    isMfaStep,
    errorMessage,
    subtitle,
    isCodeComplete,
    onSubmit,
    clearError,
  } = useSignInForm();

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <AuthFormHeading title={isMfaStep ? "Girişinizi doğrulayın" : "Hesabınıza giriş yapın"}>{subtitle}</AuthFormHeading>

          {isMfaStep ? (
            <MfaStep
              errorMessage={errorMessage}
              isSubmitting={form.formState.isSubmitting}
              isCodeComplete={isCodeComplete}
              onCodeChange={clearError}
            />
          ) : (
            <CredentialsStep
              errorMessage={errorMessage}
              isSubmitting={form.formState.isSubmitting}
            />
          )}
        </form>
      </Form>
    </div>
  );
}
